import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, EnvelopeIcon, ChevronDownIcon, PencilIcon, CheckIcon, EyeIcon } from '@heroicons/react/24/outline';
import { emailAPI, type EmailTemplate, authAPI } from '../services/api';
import { userAssistantConfigService, type UserAssistantConfig } from '../services/userAssistantConfigService';
import { EmailTemplateRichEditor } from './email/EmailTemplateRichEditor';
import { isEmailTemplateBodyEmpty } from '../utils/emailTemplateHtml';

type BulkLead = { id: string; name: string; email: string };

type BulkEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  leads: BulkLead[];
  setToast: (t: { type: 'success' | 'error'; message: string } | null) => void;
};

function fillPlaceholders(text: string, leadName: string, agentName: string, callbackNumber: string): string {
  if (!text) return text;
  let result = text;
  result = result.replace(/\{\{lead_name\}\}/g, leadName || '');
  result = result.replace(/\{\{agent_name\}\}/g, agentName || '');
  result = result.replace(/\{\{callback_number\}\}/g, callbackNumber || '');
  return result;
}

const BulkEmailModal: React.FC<BulkEmailModalProps> = ({ isOpen, onClose, leads, setToast }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [sending, setSending] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [isEditingBody, setIsEditingBody] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const subjectRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [agentName, setAgentName] = useState('');
  const [callbackNumber, setCallbackNumber] = useState('');
  const [previewLeadId, setPreviewLeadId] = useState<string | null>(null);

  // Leads with no email — bulk send will skip them server-side, surface here
  const sendableLeads = leads.filter((l) => l.email && l.email.trim());
  const skippedCount = leads.length - sendableLeads.length;

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId('');
    setEditedSubject('');
    setEditedBody('');
    setIsEditingSubject(false);
    setIsEditingBody(false);
    setProgress(null);
    setPreviewLeadId(sendableLeads.length > 0 ? sendableLeads[0].id : null);
    setLoading(true);
    emailAPI
      .getTemplates()
      .then((list) => setTemplates(Array.isArray(list) ? list : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));

    Promise.all([
      userAssistantConfigService.getConfig().catch(() => null as UserAssistantConfig | null),
      authAPI.getCurrentUser().catch(() => null as any),
    ]).then(([cfg, u]) => {
      const first = (u?.first_name || '').trim();
      const last = (u?.last_name || '').trim();
      const fullName = [first, last].filter(Boolean).join(' ').trim();
      setAgentName(fullName || (cfg?.agent_name || '').trim() || (u?.email || '').trim() || '');
      setCallbackNumber((u?.phone || '').trim());
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, leads]);

  const selectedTemplate = templates.find((t) => t.id === selectedId);

  const prevSelectedIdRef = useRef('');
  useEffect(() => {
    if (selectedId && selectedId !== prevSelectedIdRef.current) {
      const tpl = templates.find((t) => t.id === selectedId);
      if (tpl) {
        setEditedSubject(tpl.subject);
        setEditedBody(tpl.body);
      }
      setIsEditingSubject(false);
      setIsEditingBody(false);
    }
    if (!selectedId) {
      setEditedSubject('');
      setEditedBody('');
      setIsEditingSubject(false);
      setIsEditingBody(false);
    }
    prevSelectedIdRef.current = selectedId;
  }, [selectedId, templates]);

  const previewLead = previewLeadId ? sendableLeads.find((l) => l.id === previewLeadId) : null;
  const previewSubject = previewLead && editedSubject
    ? fillPlaceholders(editedSubject, previewLead.name, agentName, callbackNumber)
    : null;
  const previewBody = previewLead && editedBody
    ? fillPlaceholders(editedBody, previewLead.name, agentName, callbackNumber)
    : null;

  const handleBulkSend = async () => {
    if (!selectedId || sendableLeads.length === 0 || !editedSubject.trim() || isEmailTemplateBodyEmpty(editedBody)) return;
    setSending(true);
    setProgress(null);
    try {
      const result = await emailAPI.bulkSendEmail({
        lead_ids: sendableLeads.map((l) => l.id),
        template_id: selectedId,
        custom_subject: editedSubject.trim(),
        custom_body: editedBody.trimEnd(),
      });
      setProgress({ sent: result.sent, failed: result.failed, total: result.total });
      if (result.failed === 0) {
        setToast({ type: 'success', message: `Email sent to all ${result.sent} leads (${result.total_credits_deducted} credits used)` });
      } else {
        setToast({ type: 'error', message: `${result.sent} sent, ${result.failed} failed out of ${result.total}` });
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || 'Bulk email failed';
      setToast({ type: 'error', message: detail });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />
      <div className="relative z-10 w-full max-w-5xl rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50 sticky top-0 bg-slate-800 z-10">
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="w-6 h-6 text-sky-400" />
            <h2 className="text-lg font-semibold text-white">Bulk Send Email</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-sky-500/20 text-sky-300 text-sm font-semibold">
              {sendableLeads.length}
            </span>
            <p className="text-sm text-gray-300">
              leads with email selected
              {skippedCount > 0 && (
                <span className="text-amber-300"> · {skippedCount} skipped (no email)</span>
              )}
              {sendableLeads.length > 0 && ' — click a lead to preview their personalized email'}
            </p>
          </div>

          {sendableLeads.length > 0 && (
            <div className="max-h-28 overflow-y-auto custom-scrollbar rounded-lg border border-slate-600/50 bg-slate-700/20">
              <div className="flex flex-wrap gap-1 p-1.5">
                {sendableLeads.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setPreviewLeadId(previewLeadId === l.id ? null : l.id)}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border transition-colors cursor-pointer ${
                      previewLeadId === l.id
                        ? 'bg-sky-500/20 border-sky-500/40 text-sky-300'
                        : 'bg-slate-700/50 border-slate-600/30 text-gray-300 hover:bg-slate-600/50 hover:border-slate-500/40'
                    }`}
                  >
                    <span className="font-medium text-white">{l.name}</span>
                    <span className="text-gray-400">{l.email}</span>
                    {previewLeadId === l.id && <EyeIcon className="w-3 h-3 text-sky-400 ml-0.5" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div ref={dropdownRef} className="relative">
            <label id="bulk-email-template-label" className="block text-sm font-medium text-gray-300 mb-1.5">
              Select template *
            </label>
            {loading ? (
              <p className="text-gray-400 text-sm">Loading templates...</p>
            ) : templates.length === 0 ? (
              <p className="text-gray-400 text-sm">
                No email templates yet.{' '}
                <a href="/email-templates" className="text-cyan-400 hover:underline">Create one</a> first.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                  aria-labelledby="bulk-email-template-label"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-left text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {selectedId ? templates.find((t) => t.id === selectedId)?.name ?? 'Choose a template' : 'Choose a template'}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {dropdownOpen && (
                  <ul role="listbox" aria-labelledby="bulk-email-template-label" className="absolute left-0 right-0 top-full mt-1 z-20 py-1 rounded-lg bg-slate-800 border border-slate-600/50 shadow-xl max-h-48 overflow-y-auto custom-scrollbar">
                    <li role="option" aria-selected={!selectedId} onClick={() => { setSelectedId(''); setDropdownOpen(false); }} className={`px-3 py-2.5 cursor-pointer text-sm truncate ${!selectedId ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}>
                      Choose a template
                    </li>
                    {templates.map((t) => (
                      <li key={t.id} role="option" aria-selected={selectedId === t.id} onClick={() => { setSelectedId(t.id); setDropdownOpen(false); }} className={`px-3 py-2.5 cursor-pointer text-sm truncate ${selectedId === t.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}>
                        {t.name}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {selectedTemplate && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-gray-400">Subject</p>
                  {isEditingSubject ? (
                    <button type="button" onClick={() => setIsEditingSubject(false)} className="flex items-center gap-1 px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors text-xs font-medium">
                      <CheckIcon className="w-3.5 h-3.5" /><span>Done</span>
                    </button>
                  ) : (
                    <button type="button" onClick={() => { setIsEditingSubject(true); setTimeout(() => subjectRef.current?.focus(), 50); }} className="flex items-center gap-1 px-2 py-0.5 rounded text-gray-400 hover:text-sky-400 hover:bg-white/10 transition-colors text-xs">
                      <PencilIcon className="w-3.5 h-3.5" /><span>Edit</span>
                    </button>
                  )}
                </div>
                {isEditingSubject ? (
                  <input ref={subjectRef} type="text" value={editedSubject} onChange={(e) => setEditedSubject(e.target.value)} className="w-full px-2.5 py-2 bg-white/5 border border-sky-500/30 rounded-lg text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
                ) : (
                  <p className="text-sm text-gray-200">{editedSubject}</p>
                )}
              </div>

              <div className="rounded-lg border border-sky-500/30 bg-sky-900/10 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-sky-400">Body to send</p>
                  {isEditingBody ? (
                    <button type="button" onClick={() => setIsEditingBody(false)} className="flex items-center gap-1 px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors text-xs font-medium">
                      <CheckIcon className="w-3.5 h-3.5" /><span>Done</span>
                    </button>
                  ) : (
                    <button type="button" onClick={() => setIsEditingBody(true)} className="flex items-center gap-1 px-2 py-0.5 rounded text-gray-400 hover:text-sky-400 hover:bg-white/10 transition-colors text-xs">
                      <PencilIcon className="w-3.5 h-3.5" /><span>Edit</span>
                    </button>
                  )}
                </div>
                {isEditingBody ? (
                  <EmailTemplateRichEditor
                    key={`bulk-edit-${selectedId}`}
                    content={editedBody}
                    onChange={setEditedBody}
                    placeholder="Edit message to send…"
                  />
                ) : (
                  <div
                    className="text-xs text-gray-200 line-clamp-6 max-h-40 overflow-hidden email-template-readonly email-template-prose"
                    dangerouslySetInnerHTML={{ __html: editedBody }}
                  />
                )}
              </div>
            </div>
          )}

          {previewLead && previewBody && selectedTemplate && (
            <div className="rounded-lg border border-cyan-500/30 bg-cyan-900/10 p-3">
              <p className="text-xs font-medium text-cyan-400 mb-1.5">
                Preview for <span className="text-white font-semibold">{previewLead.name}</span>
                <span className="text-gray-400 ml-1">({previewLead.email})</span>
              </p>
              <p className="text-xs font-medium text-gray-400 mb-1">Subject</p>
              <p className="text-sm text-gray-200 mb-2">{previewSubject}</p>
              <p className="text-xs font-medium text-gray-400 mb-1">Body</p>
              <div
                className="text-sm text-gray-200 email-template-readonly email-template-prose"
                dangerouslySetInnerHTML={{ __html: previewBody }}
              />
            </div>
          )}

          {progress && (
            <div className={`rounded-lg p-3 text-sm ${progress.failed === 0 ? 'bg-emerald-900/20 border border-emerald-500/30 text-emerald-300' : 'bg-amber-900/20 border border-amber-500/30 text-amber-300'}`}>
              Sent: <strong>{progress.sent}</strong> · Failed: <strong>{progress.failed}</strong> · Total: <strong>{progress.total}</strong>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors">
              {progress ? 'Close' : 'Cancel'}
            </button>
            {!progress && (
              <button
                type="button"
                onClick={handleBulkSend}
                disabled={
                  sending || loading || !selectedId || templates.length === 0 ||
                  sendableLeads.length === 0 || !editedSubject.trim() ||
                  isEmailTemplateBodyEmpty(editedBody)
                }
                className="flex-1 px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? `Sending to ${sendableLeads.length} leads...` : `Send email to ${sendableLeads.length} leads`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkEmailModal;
